/**
 * Bridge for the src-tauri online (mic) recording chunk-upload IPC commands
 * (APP-6508).
 *
 * When the `release-apps-rust-chunk-upload-desktop` flag is on, Rust owns the online
 * chunk-upload loop (cadence timer, extraction, multipart upload, backup,
 * `:finish`). The frontend just hands over the auth token, endpoints, and
 * session/recording ids at start, pushes a refreshed token on a 401, and stops
 * the session — it never fetches audio bytes back over IPC.
 *
 * Mirrors `src/tauri/chronicle/session.ts` (same `IpcResponse` envelope
 * unwrap; no toast — callers own their error UX).
 */
import { isTauri } from '@/tauri/utils';
import type { StopReason } from '@/types/self-healing-stop';

// ---------------------------------------------------------------------------
// Types (mirror the Rust structs in src-tauri/src/ipc/online_chunk_upload.rs)
// ---------------------------------------------------------------------------

export interface StartOnlineChunkUploadParams {
  sessionId: string;
  recordingId: string;
  /** ml-scribe base URL (`getScribeBaseUrlFromV2()`). */
  baseUrl: string;
  token: string;
  /**
   * Whether chunks upload to the EKS transcription service
   * (`/transcribe-chunk`) vs ml-scribe v1. Locked for the session in Rust.
   */
  useTranscriptionServiceV2: boolean;
  /**
   * EKS transcription base URL (`getTranscriptionBaseUrl()` — EKS internal /v1
   * path). Required when `useTranscriptionServiceV2` is true; `null` for v1.
   */
  transcriptionBaseUrl: string | null;
  /** BCP-47 language codes (v2 only; FE still owns the register PUT). */
  languages: string[];
  /** Cadence between chunk extractions (seconds); PostHog `desktop-chunk-size` (default 45). */
  chunkIntervalSecs: number;
  /**
   * When true, Rust force-refreshes Auth0 on chunk-upload 401. Locked at session start.
   */
  rustAuth0TokenRefresh: boolean;
}

export interface StopOnlineChunkUploadResult {
  totalChunks: number;
  totalBytes: number;
  failedChunks: number;
  durationMs: number;
  /**
   * Whether the authoritative full-recording backup (built from the lossless
   * source WAVs) was uploaded. The stop flow keeps the local WAVs + prompts a
   * backup upload when this is `false`, instead of advancing to note generation.
   */
  backupUploaded: boolean;
  /**
   * Whether `:finish` succeeded (always `true` for v2 EKS, which auto-finalises;
   * for v1 a failed `:finish` leaves the transcription session unfinalised). The
   * stop flow gates note generation on this together with `backupUploaded` — a
   * failed finalize must not advance to note gen on a possibly-incomplete
   * transcript.
   */
  finishSucceeded: boolean;
  /**
   * The backup MP3 was retained (encrypted + a durable `backup_recovery` row)
   * and its upload detached from the stop, so it is safe but not yet confirmed
   * uploaded. Mutually exclusive with `backupUploaded` and `backupRetained`.
   *
   * This is the honest signal for a detached backup mux: the stop-side flag
   * only *requests* the detach, and the prepare phase declines it when it
   * cannot move the shared source WAVs somewhere private, falling back to an
   * inline extraction. Only this field distinguishes "still uploading" from
   * "the extraction failed and there is no backup".
   */
  backupPendingAsync: boolean;
  /**
   * The blocking backup upload failed or exceeded the stop budget, so the MP3
   * was retained (encrypted + a durable row) for the boot reconcile. No data
   * loss, but nothing is uploading right now.
   */
  backupRetained: boolean;
  /**
   * Whether this stop MOVED its source WAVs off the live paths. Two mechanisms
   * set it, and the flag deliberately does not distinguish them, because what
   * the caller must not do is the same either way: the device-change stop
   * ROTATES the pair to unique sibling paths for a detached tail, and any stop
   * may SNAPSHOT it into the session's own directory so the mux can outlive the
   * stop. In both cases the live WAV paths no longer hold this session's audio.
   *
   * Note it is not "the mux is still running" — a snapshot that was taken and
   * then muxed inline still reports `true`, because the paths are vacated
   * regardless of who ran the mux.
   *
   * Read it alongside `backupUploaded`, never instead of it. `backupUploaded
   * === false` alone cannot tell "extraction FAILED, so the WAVs at the live
   * paths are the only copy — preserve them" from "extraction has not run YET,
   * and the tail owns rotated copies". Preserving on the second reading FFmpegs
   * paths that rotation already renamed away: it cannot succeed, and it burns
   * an `FFMPEG_SEMAPHORE` slot inside the recording-start critical path.
   */
  backupDeferred: boolean;

  /**
   * WHY THIS STOP HAPPENED — as recorded on the terminal outcome this call
   * resolved to, which is NOT always the stop this call issued.
   *
   * **OPTIONAL, and that is the honest declaration rather than a convenience.**
   * Rust declares it non-optional on `TerminalOutcome`, so a real IPC response
   * always carries it — but this interface is also the shape of values the FE
   * SYNTHESISES without any stop response at all, such as
   * `terminalOutcomeAsStopResult`'s projection of a relayed
   * `self_healing:terminal_outcome` event. Those genuinely have no reason to
   * report, and a required field would make them lie. The one consumer that
   * reads this treats absence as "cannot tell", which is the same answer it
   * gives a reason that is not ours.
   *
   * **This field is the only thing distinguishing a stop we performed from a
   * replay of somebody else's.** `terminal::execute` step 1
   * (`src-tauri/src/recording_lifecycle/terminal.rs`) returns
   * `Ok(cached_outcome)` for an epoch that is ALREADY TERMINAL — it re-runs no
   * pipelines and stops nothing, and the Rust test
   * `execute_idempotent_replays_cached_outcome_without_touching_pipelines` in
   * that same file pins it with `.expect("cached outcome replays as Ok")`.
   * Nothing ever clears the
   * epoch (`RecordingLifecycle::mark_terminal` moves the phase only, and
   * `current_identity()` has no phase filter), so after ANY completed recording
   * in an app run, an identity-less stop resolves to that terminal epoch and
   * replays it. `Ok` therefore does not mean "we stopped something".
   *
   * The reason does. A stop that genuinely ran carries back the reason WE sent
   * (`outcome_from_stop_result` copies the argument onto the outcome verbatim);
   * a replay carries back the reason of the ORIGINAL stop, typically
   * `'userStop'`. `SessionEndSweepStopOutcome`
   * (`src/tauri/online-chunk-upload/session-end-sweep.ts`) is the one consumer
   * that reads it that way, and the reason this field is declared at all.
   */
  reason?: StopReason;

  /**
   * The epoch this stop resolved to. Diagnostic only — **not** a replay
   * discriminator, and deliberately not used as one.
   *
   * A sweep's stop carries no `recordingId`/`generation` of its own (Rust falls
   * back to `current_identity()`), and the probe answers with a `sessionId`, so
   * the client holds no expected epoch to compare these against. What they buy
   * is that the log line names WHICH epoch was reached — which is how the one
   * case `reason` alone cannot catch (a second sweep replaying a first sweep's
   * own `feFallback` terminal) is still visible in the fleet: two sweep lines
   * in one app run naming the same `recordingId` + `generation`.
   *
   * Optional for the same reason as `reason` above: an FE-synthesised result
   * names no epoch.
   */
  recordingId?: string;
  generation?: number;
  /**
   * The local offline consult this stop handed its unsent audio to. Present
   * only when the stop sent `offlineHandover` and the network was down: the
   * reconnect sync uploads that audio into this session and generates the
   * note. It is what tells an offline-held session apart from one that needs an
   * upload now, since `failedChunks` still counts the chunks that failed.
   */
  offlineHandoverLocalSessionId?: string;
}

/**
 * The session settings a stop sends so Rust can hand audio it could not upload
 * to the offline queue (`src-tauri/src/offline_recording/handover.rs`). Sent
 * only with the offline-recording flag on. `dictationType` matters most: the
 * sync writes the transcribe mode back onto the server session, so omitting it
 * would switch a Dictate session to Transcribe.
 */
export interface OfflineHandoverArgs {
  templateId?: string;
  dictationType?: string;
  language?: string;
  outputLanguage?: string;
  noteStyle?: string;
}

/** Whether Rust still owns an online-chunk session after WebView reload. */
export interface ActiveOnlineChunkUpload {
  active: boolean;
  sessionId: string | null;
}

// ---------------------------------------------------------------------------
// Internal invoke helper
// ---------------------------------------------------------------------------

class OnlineChunkUploadBridgeError extends Error {
  /** Server-supplied error code, present when the Rust IPC handler errored. */
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'OnlineChunkUploadBridgeError';
    this.errorCode = errorCode;
  }
}

/** Shape Rust returns via `Ok(IpcResponse::from(...))`. See `ipc/response.rs`. */
type IpcResponseEnvelope<D> = {
  result?: { data: D } | null;
  error?: { message: string; error_code?: string } | null;
};

async function invokeCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new OnlineChunkUploadBridgeError(
      'Online chunk upload bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  const response = await invoke<IpcResponseEnvelope<T>>(command, payload ?? {});

  if (response?.error != null) {
    throw new OnlineChunkUploadBridgeError(
      response.error.message,
      response.error.error_code
    );
  }
  if (response?.result == null) {
    throw new OnlineChunkUploadBridgeError(
      `online chunk upload bridge: ${command} returned malformed IpcResponse`
    );
  }
  return response.result.data;
}

// ---------------------------------------------------------------------------
// Public bridge
// ---------------------------------------------------------------------------

export const onlineChunkUploadBridge = {
  /** Configure + start the Rust upload pipeline (recorder already started). */
  async startSession(params: StartOnlineChunkUploadParams): Promise<void> {
    return invokeCommand<void>('online_chunk_upload_start', { params });
  },

  /** Push a fresh token after an `online_chunk:token_refresh_needed` event. */
  async refreshToken(token: string): Promise<void> {
    return invokeCommand<void>('online_chunk_upload_refresh_token', {
      params: { token },
    });
  },

  /** Whether Rust still has an online-chunk session (for boot re-attach). */
  async getActive(): Promise<ActiveOnlineChunkUpload> {
    return invokeCommand<ActiveOnlineChunkUpload>('online_chunk_upload_get_active');
  },

  /**
   * Stop the session. By default (final stop) blocks until the recorder is stopped
   * and the final chunk + backup are uploaded, so note generation is safe afterwards.
   *
   * Pass `{ backgroundBackup: true }` on a device-change recovery (APP-7375): Rust
   * keeps the backup *extraction* synchronous (a restart truncates the source WAVs)
   * but runs the upload tail — final-chunk drain + backup upload + `:finish` —
   * detached, so the new mic restarts in ~1s instead of waiting ~4s for the network.
   */
  async stopSession(opts?: {
    backgroundBackup?: boolean;
    /** Never read the source WAV after a terminal writer failure. */
    quarantineWav?: boolean;
    /**
     * APP-8723: the self-healing-stop epoch this stop targets. Optional —
     * when omitted, Rust falls back to `RecordingLifecycle`'s current epoch,
     * so existing callers keep working unchanged.
     */
    recordingId?: string;
    generation?: number;
    /**
     * FR-036/FR-037: why this stop happened. Omit for the final user stop —
     * Rust defaults to `userStop`, so callers that predate this keep today's
     * behaviour. `pause` and `deviceChange` mark a **non-generating** segment
     * stop: the recording still finalizes, drains and retains with no data
     * loss, but note generation is suppressed and resume / the new device
     * claims a fresh epoch in the same session.
     *
     * It has to be passed *here*, at the stop, rather than reported
     * afterwards: the reason is recorded into the terminal outcome and the
     * emitted event, and a later correction would be a second terminal call
     * against an already-terminal epoch.
     */
    reason?: StopReason;
    /**
     * Request to run the backup FFmpeg mux detached instead of awaiting it
     * inline. The FE sets it from the `release-desktop-detached-backup-mux`
     * kill switch (default OFF), which detaches for **any** recording,
     * including a pause. A transcription-service V2 recording also detaches
     * (the chunk transcript is what note generation reads, so the backup must
     * not gate it), but only on a note-generating stop and **never** on a
     * pause, so the single detached-mux slot stays free for the stop that
     * actually gates note generation. Rust may
     * still decline (`admit_detached_mux`) and mux inline; the result's
     * `backupPendingAsync` / `backupUploaded` say which happened.
     * Independent of the self-healing-stop flag — it carries no payload and
     * changes nothing else about the stop.
     */
    detachedBackupMux?: boolean;
    /**
     * Session settings for handing unsent audio to the offline queue. Omit it
     * and the stop never hands over, exactly as before the handover existed.
     */
    offlineHandover?: OfflineHandoverArgs;
  }): Promise<StopOnlineChunkUploadResult> {
    return invokeCommand<StopOnlineChunkUploadResult>('online_chunk_upload_stop', {
      params: {
        backgroundBackup: opts?.backgroundBackup ?? false,
        detachedBackupMux: opts?.detachedBackupMux ?? false,
        quarantineWav: opts?.quarantineWav ?? false,
        recordingId: opts?.recordingId,
        generation: opts?.generation,
        reason: opts?.reason,
        offlineHandover: opts?.offlineHandover,
      },
    });
  },
};

export { OnlineChunkUploadBridgeError };
